import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import {
  ChakraAutocompleteField,
  DateField,
  InputField,
  MessageBox,
} from 'app/components/Core';
import React from 'react';
import { ReleaseExport, TuneCoreRelease } from 'types/releases';
import { useForm } from 'react-hook-form';
import { dynamicYupResolver } from 'app/utils/utils';
import { JSONSchema7 } from 'json-schema';
import { Option } from 'types/general.types';
import { AccountConnectionProviderId, Connection } from 'types/profile';
import { ExportTuneCoreReleaseFormValues } from './types';

interface ExportTuneCoreReleaseDialogComponentProps {
  setIsOpen: (isOpen: boolean) => void;
  release: TuneCoreRelease;
  defaultValues: ExportTuneCoreReleaseFormValues;
  validations: JSONSchema7;
  genres: Option[];
  languages: Option[];
  connection?: Connection;
  exportRelease: (
    data: ExportTuneCoreReleaseFormValues,
    connectionGuid?: string
  ) => Promise<ReleaseExport>;
  checkConnectionStatus: (
    providerId: AccountConnectionProviderId
  ) => Promise<string | undefined>;
  afterExport: (
    providerId: AccountConnectionProviderId,
    exportGuid: string,
    connectionGuid?: string
  ) => void;
}

export const ExportTuneCoreReleaseDialogComponent = ({
  setIsOpen,
  release,
  defaultValues,
  validations,
  languages,
  genres,
  connection,
  exportRelease,
  checkConnectionStatus,
  afterExport,
}: ExportTuneCoreReleaseDialogComponentProps) => {
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ExportTuneCoreReleaseFormValues>({
    defaultValues,
    mode: 'all',
    context: { yupValidationSchema: validations },
    resolver: dynamicYupResolver,
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      const renewedConnectionGuid = await checkConnectionStatus('TuneCore');
      const response = await exportRelease(data, renewedConnectionGuid);
      afterExport('TuneCore', response.guid, renewedConnectionGuid);
      setIsOpen(false);
    } catch (e) {
      // proceed
    }
  });

  return (
    <Modal
      size="md"
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={() => setIsOpen(false)}
      scrollBehavior="inside"
      autoFocus={false}
    >
      <ModalOverlay />
      <ModalContent data-testid="dialog" sx={{ minH: 280 }}>
        <ModalHeader data-testid="dialog-title" title="Export Release">
          Export Release
        </ModalHeader>
        <ModalBody data-testid="dialog-content" py={0}>
          <VStack as="form" spacing={0}>
            <InputField
              label={
                release.versions.length > 1 ? 'Album Title' : 'Single Title'
              }
              type="text"
              value={release.title}
              isDisabled
              overflow="hidden"
              textOverflow="ellipsis"
              data-testid="title"
            />
            <SimpleGrid width="full" columns={2} spacingX={4}>
              <InputField
                label="Main Artist"
                type="text"
                overflow="hidden"
                textOverflow="ellipsis"
                {...register('mainArtist')}
                data-testid="title"
                isInvalid={Boolean(errors.mainArtist)}
                errorMessage={errors.mainArtist?.message}
              />
              <ChakraAutocompleteField
                control={control}
                name="genre"
                label="Genre"
                menuPosition="fixed"
                options={genres}
              />
              <DateField
                control={control}
                name="releaseDate"
                label="Release Date"
                shouldSaveInvalidDate
                tooltip="TIP: Set your release date 4 weeks from today to give stores time to review your release"
              />
              <ChakraAutocompleteField
                control={control}
                name="language"
                label="Language"
                options={languages}
                menuPosition="fixed"
              />
            </SimpleGrid>
            <InputField
              label="Recording Location"
              type="text"
              overflow="hidden"
              textOverflow="ellipsis"
              {...register('recordingLocation')}
              isInvalid={Boolean(errors.recordingLocation)}
              errorMessage={errors.recordingLocation?.message}
            />
            {!connection && (
              <MessageBox
                boxProps={{
                  w: 'full',
                  'data-testid': 'info-message',
                }}
              >
                You need to connect to TuneCore to export
              </MessageBox>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Stack spacing={4} direction="row">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={onSubmit} isDisabled={isSubmitting}>
              {connection ? 'Export' : 'Connect & Export'}
            </Button>
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
